import { ChangeEvent, FC, useRef } from 'react';

import UploadDocument, {
  UploadDocumentSizes,
} from 'modules/documents/components/UploadDocument';
import { useTranslate } from 'hooks/useTranslate';

import { useSharesTransferExcelModal } from '../context/SharesTransferExcelModalContext';

const TYPE_FILES_ALLOWED = '.xls,.xlsx';

const SharesTransferExcelUploadView: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useSharesTransferExcelModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChangeFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      actions.handleChangeExcelFile(selectedFile);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTriggerChangeFile = () => {
    fileInputRef.current?.click();
  };

  const uploadRules = [
    t('SharesTransferExcelUploadRuleSheet'),
    t('SharesTransferExcelUploadRuleOperationId'),
    t('SharesTransferExcelUploadRuleSign'),
    t('SharesTransferExcelUploadRuleDate'),
  ];

  return (
    <div className="shares-transfer-excel-modal__upload">
      <div className="card card-bordered shares-transfer-excel-modal__upload-card">
        <div className="card-inner">
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap">
            <div className="d-flex">
              <em className="icon ni ni-file-xls text-primary mr-2 mt-1" />
              <div>
                <h6 className="mb-1">{t('SharesTransferExcelUploadCardTitle')}</h6>
                <p className="text-soft small mb-0">
                  {t('SharesTransferExcelUploadCardDescription')}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm mt-2 mt-md-0"
              onClick={actions.handleDownloadExcelTemplate}
              data-testid="shares-transfer-excel-download-template"
            >
              <em className="icon ni ni-download" />
              <span className="ml-1">
                {t('SharesTransferExcelDownloadTemplate')}
              </span>
            </button>
          </div>

          {data.excelFile && !data.isParsingExcel ? (
            <div
              className="shares-transfer-excel-modal__file-chip d-flex align-items-center justify-content-between mb-3"
              data-testid="shares-transfer-excel-file-chip"
            >
              <div className="d-flex align-items-center">
                <em className="icon ni ni-file-xls text-primary mr-2" />
                <span className="font-weight-bold">{data.excelFile.name}</span>
              </div>
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={handleTriggerChangeFile}
                data-testid="shares-transfer-excel-change-file"
              >
                {t('SharesTransferExcelChangeFile')}
              </button>
            </div>
          ) : (
            <UploadDocument
              inputLabel={t('SharesTransferExcelUploadDragHint')}
              file={data.excelFile}
              setFile={actions.handleChangeExcelFile}
              accept={TYPE_FILES_ALLOWED}
              skipFileValidation
              size={UploadDocumentSizes.LARGE}
              idIndex={2}
            />
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={TYPE_FILES_ALLOWED}
            className="d-none"
            onChange={handleChangeFileInput}
            data-testid="shares-transfer-excel-hidden-file-input"
          />

          {data.isParsingExcel && (
            <div
              className="shares-transfer-excel-modal__loading-strip d-flex align-items-center mt-3"
              data-testid="shares-transfer-excel-loading-strip"
            >
              <div
                className="spinner-border spinner-border-sm text-primary mr-2"
                role="status"
              />
              <span className="text-soft">
                {t('SharesTransferExcelValidatingAndLoading')}
              </span>
            </div>
          )}

          <ul className="shares-transfer-excel-modal__upload-rules text-soft small mb-0 mt-3">
            {uploadRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>

          <p className="text-soft small mb-0 mt-2">
            {t('SharesTransferExcelTemplateHint')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharesTransferExcelUploadView;
